import type { JSONContent } from '@tiptap/react';
import { get, patch, del, post, put } from '../utils/networking';

export type Team = {
  id: number;
  name: string;
  numberOfTeamMembers: number;
  organizationId: number;
  tenantId: number;
  averageScore: number;
  maxPossibleScore: number;
  standardDeviation?: number;
  completedCustomSurveysCount?: number;
  activeCustomSurveysCount?: number;
  workLanguage?: 'NB' | 'EN' | null;
  createdAt: Date;
};

export type Organization = {
  id: number;
  name: string;
  orgNumber: string;
  tenantId: number;
  createdAt: Date;
  auth0Id: string;
  workLanguage: 'NB' | 'EN' | null;
};

export type Survey = {
  id: number;
  uuid: string;
  title: string;
  description: string | null;
  isActive?: boolean;
  questions: Array<{
    id: number;
    question: string;
    type: string;
    scale: number | null;
    shouldReverseScore: boolean | null;
    explanation?: string;
  }>;
};

export type PublicSurvey = {
  uuid: string;
  title: string;
  description: string | null;
};

export type QuestionWithScore = {
  id: number;
  question: string;
  score: number;
};

export type QuestionsWithScore = Array<QuestionWithScore>;

export type AnalyzeSurveyTips = {
  what: string;
  why: string;
  how: string;
  danger: string;
  examplesOfHow: string;
};

export type AnalyzeSurveyResultTips = {
  questionNumber?: number;
  questionId: number;
  question: string;
  questionExplanation: string;
  score: number;
  standardDeviation?: number;
  responseCount?: number;
  consequences: Array<string>;
  tips: Array<AnalyzeSurveyTips>;
};

export type AnalyzeSurvey = {
  surveyTitle: string;
  code: 'COMPLETED' | 'NOT_COMPLETED';
  data: Array<AnalyzeSurveyResultTips>;
  message?: string;
};

export type SurveyWithinTimeFrame = {
  title: string;
  date: Date;
  averageScore: number;
};

export type QuestionTrendDataPoint = {
  date: string;
  score: number;
  responseCount: number;
};

export type QuestionTrend = {
  question: {
    id: number;
    surveyId: number;
    question: string;
  };
  trends: QuestionTrendDataPoint[];
};

export type QuestionTrendsResponse = {
  questionTrends: QuestionTrend[];
};

export type PastSurveyTableRowProps = {
  surveyId: number;
  date: string;
  status: string;
  score: number | string;
  standardDeviation?: number;
  deadlineInDays?: number;
  responseRateAsNumber: number;
  isActive: boolean;
  createdAt: Date;
};

export type LatestSurveyScore = {
  currentScore: number;
  maxScore: number;
  standardDeviation?: number;
};

export type TeamSurveys = {
  id: number;
  title: string;
  createdAt: Date;
  isActive: boolean;
  /** Null når målingen ikke har nok svar til å vise resultat (eller ikke er avsluttet og analysert) */
  score: number | null;
  standardDeviation?: number;
  deadlineInDays?: number;
  responseRate: number;
  numberOfRespondents: number;
  numberOfTeamMembers: number;
  expectedParticipantsCount: number;
  analyzeStatus: 'NOT_ANALYZED' | 'COMPLETED' | 'INVALID_RESPONSE_RATE';
};

export enum DistributionChannelTypeEnum {
  Slack = 'Slack',
  MicrosoftTeams = 'Microsoft Teams',
  Email = 'E-post',
  SMS = 'SMS',
}

export type DistributionChannel = {
  id: number;
  type: DistributionChannelTypeEnum;
  webhookUrl: string | null;
  teamsChannelName?: string | null;
  slackChannelName?: string | null;
};

export type TeamLeader = {
  id: number;
  userId: number;
  pending: boolean;
  isLeader: boolean;
  email: string;
};

export enum UserRoles {
  ORG_ADMIN = 'ORG_ADMIN',
  MEMBER = 'MEMBER',
  TEAM_LEAD = 'TEAM_LEAD',
  SUPER_ADMIN = 'SUPER_ADMIN',
  CONTENT_CREATOR = 'CONTENT_CREATOR',
  HR_ADMIN = 'HR_ADMIN',
  PARTNER_ADMIN = 'PARTNER_ADMIN',
  SAFETY_REP = 'SAFETY_REP',
  GENERAL_MANAGER = 'GENERAL_MANAGER',
}

export type Userinfo = {
  nickname: string;
  email: string;
  userRoles: Array<UserRoles>;
  /** Leads at least one team, and so has the team leader's part of the app */
  leadsTeam?: boolean;
  isSsoUser: boolean;
};

export type PlannedSurvey = {
  id: number;
  title: string;
  description: string;
  templateId: number;
  scheduledTime: Date;
  frequency?: string;
  isActive: boolean;
  deadlineInDays?: number | undefined;
  daysBeforeDeadlineToNotify?: number | undefined;
  createdAt: string;
  surveyType: 'PSYCHOLOGICAL_SAFETY' | 'WORK_ENVIRONMENT';
  actionProposalDeadlineInDays?: number | null;
  actionVotingDeadlineInDays?: number | null;
  expectedParticipantsCount?: number | null;
  nextScheduledMeasurement?: Date | null;
};

export type NumberStatsCount = {
  count: number;
};
export type QuestionScoreComparison = {
  id: number;
  question: string;
  previousScore: number;
  currentScore: number;
  difference: number;
  currentStandardDeviation?: number;
  previousStandardDeviation?: number;
};

export type AreaOfChange = {
  id: number;
  title: string;
  description: string;
};

type SurveyScoreComparison = {
  id: number;
  currentScore: number;
  previousScore: number;
  scoreDifference: number;
  scoreDifferenceDirection: 'POSITIVE' | 'NEGATIVE' | 'EQUAL';
  currentStandardDeviation?: number;
  previousStandardDeviation?: number;
  standardDeviationDifference?: number;
};

export type SurveySummary = {
  SurveyQuestionScoreComparison: Array<QuestionScoreComparison>;
  areasOfChange: Array<AreaOfChange>;
  overview: string;
  surveyScoreComparison: SurveyScoreComparison;
  currentSurveyScore: number;
  lastSurveyScore: number;
};

export type OrganizationUser = {
  id: number;
  email: string;
  roles: Array<string>;
  teamNames: Array<string>;
  /** Set when the user is a safety representative */
  safetyRep: { isMain: boolean; teams: Array<{ id: number; name: string }> } | null;
  isPending: boolean;
  createdAt: Date;
};

/** Which teams a safety representative represents, and whether they are the main one */
export type SafetyRepAssignment = { isMain: boolean; teamIds: Array<number> };

export type TeamHealthMetrics = {
  id: number;
  category: string;
  description: string;
  score: number;
  standardDeviation?: number;
  surveyId?: number;
  createdAt?: string;
  tenantId?: number;
};

export type Label = {
  id: number;
  name: string;
  description?: string;
  color: string;
};

type ArticleJsonBlockContent =
  | {
      id: string;
      type: 'header';
      data: {
        text: string;
        level: number;
      };
    }
  | {
      id: string;
      type: 'paragraph';
      data: {
        text: string;
      };
    }
  | {
      id: string;
      type: 'image';
      data: {
        file: {
          url: string;
        };
        caption: string;
        stretched: boolean;
        withBorder: boolean;
        withBackground: boolean;
      };
    };

export type ArticleJsonContent = {
  time: Date;
  blocks: ArticleJsonBlockContent[];
};

export type ArticleRequest = {
  title: string;
  isDraft: boolean;
  draftId: number;
  coverImageUrl: string;
  coverImageAlt: string;
  jsonContent: JSONContent;
  isGlobalGuide: boolean;
  labels: Array<Label>;
  updatedAt: Date;
};

export type ArticleResponse = {
  id: number;
  title: string;
  isDraft: boolean;
  coverImageUrl: string;
  coverImageAlt: string;
  jsonContent: JSONContent;
  isGlobalGuide: boolean;
  labels: Label[];
  updatedAt: Date;
  draftId?: number;
  createdByUsers: Array<{
    email: string;
  }>;
};

export type ArticleDraftResponse = {
  guideId: number;
} & ArticleResponse;

export type ArticleDraftRequest = {
  guideId?: number;
} & ArticleRequest;

export type GetArticlesRequestTypes = {
  pageNumber: number;
  pageSize: number;
  sortBy: 'asc' | 'desc';
  labelIds?: string;
  isGlobalGuide: boolean;
};
export type OrganizationHealthMetrics = TeamHealthMetrics;

export type TeamUsageStats = {
  teamId: number;
  teamName: string;
  channels: string[];
  totalSurveysSent: number;
  activeSurveysCount: number;
  completedSurveysCount: number;
  totalResponses: number;
  averageResponseRate: number;
  scheduledSurveysCount: number;
  recurringScheduledSurveysCount: number;
  lastSurveyDate: Date | null;
};

export type OrganizationUsageStats = {
  totalTeamsCount: number;
  teamsWithActiveSurveysCount: number;
  teamsWithScheduledSurveysCount: number;
  totalSurveysSent: number;
  totalResponsesReceived: number;
  averageResponseRate: number;
  totalScheduledSurveys: number;
  recurringScheduledSurveys: number;
  automationStats: AutomationStats;
  teamUsageStats: TeamUsageStats[];
  periodStart: Date | null;
  periodEnd: Date | null;
};

export const fetchAllTeams = async (): Promise<Array<Team>> =>
  await get('/api/teams');
export const fetchAllTeamsForHr = async (): Promise<Array<Team>> =>
  await get('/api/teams/all-for-hr');
export const fetchTeamByTeamId = async (teamId: number): Promise<Team> =>
  await get(`/api/teams/${teamId}`);

export const fetchSurveyByUUID = async (uuid: string, language?: string): Promise<Survey> => {
  const params = language ? `?language=${language}` : '';
  return await get('/api/surveys/anonymous/' + uuid + params);
};

export const fetchAllTeamsByTeamId = async (teamId: number): Promise<Survey> =>
  await get('/api/surveys/' + teamId);

export const getLatestSurveyScore = async (
  teamId: number,
): Promise<LatestSurveyScore> => {
  return await get(`/api/dashboard/team/${teamId}/get-latest-survey-score`);
};

export const getDistributionChannels = async (
  teamId: number,
): Promise<Array<DistributionChannel>> => {
  return await get(`/api/distribution-channels/${teamId}`);
};

export const fetchQuestionsWithScore = async (
  fromDate: string,
  toDate: string,
  teamId: number,
): Promise<QuestionsWithScore> =>
  await get(`/api/dashboard/team/${teamId}/questions-scores`, {
    params: {
      fromDate: `${fromDate}T00:00:00.000Z`,
      toDate: `${toDate}T23:59:00.000Z`,
    },
  });

export const fetchSurveysWithinTimeframe = async (
  teamId: number,
  fromDate: string,
  toDate: string,
): Promise<Array<SurveyWithinTimeFrame>> =>
  await get(`/api/dashboard/team/${teamId}/surveys-within-timeframe`, {
    params: {
      fromDate: `${fromDate}T00:00:00.000Z`,
      toDate: `${toDate}T23:59:00.000Z`,
    },
  });

export const fetchQuestionTrends = async (
  teamId: number,
  fromDate: string,
  toDate: string,
): Promise<QuestionTrendsResponse> =>
  await get(`/api/dashboard/team/${teamId}/question-trends`, {
    params: {
      fromDate: `${fromDate}T00:00:00.000Z`,
      toDate: `${toDate}T23:59:00.000Z`,
    },
  });

export const fetchAnalyzeSurvey = async (id: number): Promise<AnalyzeSurvey> =>
  await get(`/api/surveys/analyzed-survey/${id}`);

export const fetchPastSurveys = async (
  teamId: number,
): Promise<Array<TeamSurveys>> => await get(`/api/surveys/team/${teamId}`);

export const getLatestSurveyByTeamId = async (
  teamId: number,
): Promise<Survey> => {
  return await get(`/api/surveys/ongoing/team/${teamId}`);
};

export const getUserinfo = async (): Promise<Userinfo> => {
  return await get('/api/auth/userinfo');
};

export const fetchTeamLeaders = async (teamId: number): Promise<TeamLeader[]> =>
  await get(`/api/teams/${teamId}/members`);
export type SurveyTemplate = {
  id: number;
  name: string;
  description: string;
  questions: Array<{
    id: number;
    text: string;
    type: string;
  }>;
};
export const fetchSurveyTemplates = async (): Promise<Array<SurveyTemplate>> =>
  await get(`/api/templates`);

export const fetchPlannedSurveys = async (
  teamId: number,
): Promise<Array<PlannedSurvey>> =>
  await get(`/api/schedule-surveys/${teamId}`);

export const getPlannedSurveyById = async (
  teamId: number,
  surveyId: number,
): Promise<PlannedSurvey> => {
  return await get(`/api/schedule-surveys/${teamId}/survey/${surveyId}`);
};

export const getScheduledCustomSurveyById = async (
  teamId: number,
  surveyId: number,
): Promise<ScheduledCustomSurvey> => {
  return await get(`/api/schedule-custom-surveys/${teamId}/survey/${surveyId}`);
};

export const getTeamsWithinOrganization = async (): Promise<Array<Team>> => {
  return await get(`/api/organization/teams`);
};

export const getOrganizationOngoingSurveysCount =
  async (): Promise<NumberStatsCount> => {
    return await get('/api/organization/stats/ongoing-surveys-count');
  };

export const getOrganizationHealthMetrics = async (): Promise<
  Array<OrganizationHealthMetrics>
> => await get(`/api/organization/stats/organization-health-metrics`);

export const getOrganizationPlannedSurveysCount =
  async (): Promise<NumberStatsCount> => {
    return await get('/api/organization/stats/planned-surveys-count');
  };

export const getOrganizationAveragePsychologicalSafetyScore =
  async (): Promise<NumberStatsCount> => {
    return await get(
      '/api/organization/stats/psychological-safety-average-count',
    );
  };

export const getOrganizationUsageStats = async (
  periodStart?: string,
  periodEnd?: string,
): Promise<OrganizationUsageStats> => {
  const params = new URLSearchParams();
  if (periodStart) params.append('periodStart', periodStart);
  if (periodEnd) params.append('periodEnd', periodEnd);

  const queryString = params.toString();
  const endpoint = `/api/organization/stats/usage${queryString ? `?${queryString}` : ''}`;

  return await get(endpoint);
};

export type ExportOrganizationUsagePdfParams = {
  periodStart?: string;
  periodEnd?: string;
};

export const exportOrganizationUsagePdf = async (
  params: ExportOrganizationUsagePdfParams,
): Promise<Blob> => {
  const urlParams = new URLSearchParams({
    key: 'organization-usage',
  });

  if (params.periodStart) {
    urlParams.append('periodStart', params.periodStart);
  }

  if (params.periodEnd) {
    urlParams.append('periodEnd', params.periodEnd);
  }

  const response = await fetch(`/api/pdf-export?${urlParams.toString()}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('PDF export failed');
  }

  return await response.blob();
};

export type ResponseRateTrendPoint = {
  surveyId: string;
  date: string;
  responsesReceived: number;
  recipientCount: number;
  responseRate: number;
};

export type ChannelDistributionItem = {
  channel: 'SMS' | 'SLACK' | 'TEAMS' | 'EMAIL' | 'UNKNOWN';
  count: number;
  percentage: number;
};

export type SurveyHistoryItem = {
  id: string;
  sentDate: string;
  channel: string;
  surveyType: 'PSYCHOLOGICAL_SAFETY' | 'CUSTOM_SURVEY' | 'WORK_ENVIRONMENT';
  status: 'ONGOING' | 'COMPLETED' | 'EXPIRED';
  recipientCount: number;
  responsesReceived: number;
  responseRate: number;
};

export type ScheduledSurveyItem = {
  id: string;
  surveyType: 'PSYCHOLOGICAL_SAFETY' | 'CUSTOM_SURVEY' | 'WORK_ENVIRONMENT';
  frequency: string;
  channel: string;
  isActive: boolean;
  nextScheduledDate: string | null;
};

export type AutomationStats = {
  remindersSent: number;
  deadlineExtensions: number;
  resultsShared: number;
  actionPlanInvitations: number;
  actionPlanNotifications: number;
  aiRecommendationsGenerated: number;
};

export type TeamUsageDetails = {
  teamId: string;
  teamName: string;
  teamSize: number;
  periodStart: string;
  periodEnd: string;
  totalSurveys: number;
  totalResponses: number;
  averageResponseRate: number;
  totalScheduledSurveys: number;
  activeScheduledSurveys: number;
  automationStats: AutomationStats;
  responseRateTrend: ResponseRateTrendPoint[];
  channelDistribution: ChannelDistributionItem[];
  surveyHistory: SurveyHistoryItem[];
  scheduledSurveys: ScheduledSurveyItem[];
};

export const getTeamUsageDetails = async (
  teamId: string,
  periodStart?: string,
  periodEnd?: string,
): Promise<TeamUsageDetails> => {
  const params = new URLSearchParams();
  if (periodStart) params.append('periodStart', periodStart);
  if (periodEnd) params.append('periodEnd', periodEnd);

  const queryString = params.toString();
  const endpoint = `/api/organization/stats/usage/${teamId}${queryString ? `?${queryString}` : ''}`;

  return await get(endpoint);
};

export const getOrganizationUsers = async (): Promise<
  Array<OrganizationUser>
> => {
  return await get(`/api/organization/users`);
};

export type UpdateUserRolesRequest = {
  roles: Array<string>;
  /** Required when the roles include SAFETY_REP */
  safetyRep?: SafetyRepAssignment;
};

export const updateUserRoles = async (
  userId: number,
  data: UpdateUserRolesRequest,
): Promise<void> => {
  return await patch(`/api/organization/users/${userId}/roles`, data);
};

export const removeUserRole = async (
  userId: number,
  roleId: number,
): Promise<void> => {
  return await del(`/api/organization/users/${userId}/roles/${roleId}`);
};

export const removeUserFromOrganization = async (
  userId: number,
): Promise<void> => {
  return await del(`/api/organization/users/${userId}`);
};

export const activateUser = async (userId: number): Promise<void> => {
  return await post(`/api/organization/users/${userId}/activate`);
};

export type InviteUserToOrganizationRequest = {
  email: string;
  roles?: Array<string>;
  /** Required when the roles include SAFETY_REP */
  safetyRep?: SafetyRepAssignment;
};

export type InviteUserToOrganizationResponse = {
  message: string;
  email: string;
  status: string;
};

export const inviteUserToOrganization = async (
  data: InviteUserToOrganizationRequest,
): Promise<InviteUserToOrganizationResponse> => {
  return await post(`/api/organization/users/invite`, data);
};

export const fetchAnalyzationSummary = async (
  surveyId: number,
): Promise<SurveySummary> =>
  get(`/api/surveys/analyzed-survey-summary/${surveyId}`);

export const getTeamHealthMetrics = async (
  teamId: number,
): Promise<Array<TeamHealthMetrics>> => {
  return await get(`/api/dashboard/team/${teamId}/team-health-metrics`);
};

export const getLabels = async (): Promise<Array<Label>> => {
  return await get(`/api/labels`);
};

export const getArticles = async ({
  pageNumber,
  pageSize,
  sortBy,
  labelIds,
  isGlobalGuide,
}: GetArticlesRequestTypes): Promise<{
  articles: Array<ArticleResponse>;
  pagination: { total: number };
}> => {
  const params = {
    pageNumber,
    pageSize,
    sortBy,
    isGlobalGuide,
    ...(labelIds && { labels: labelIds }),
  };
  return await get(`/api/guides`, { params });
};

export const getDraftArticles = async ({
  pageNumber,
  pageSize,
  sortBy,
  labelIds,
  isGlobalGuide,
}: GetArticlesRequestTypes): Promise<{
  draftArticles: Array<ArticleDraftResponse>;
  pagination: { total: number };
}> => {
  const params = {
    pageNumber,
    pageSize,
    sortBy,
    isGlobalGuide,
    ...(labelIds && { labels: labelIds }),
  };
  return await get(`/api/guides/draft`, { params });
};

export const getArticleById = async ({
  id,
}: {
  id: number;
}): Promise<ArticleResponse> => {
  return await get(`/api/guides/${id}`);
};

export const getDraftArticleById = async ({
  draftId,
}: {
  draftId: number;
}): Promise<ArticleDraftResponse> => {
  return await get(`/api/guides/draft/${draftId}`);
};

export const createArticleWithDraft = async ({
  article,
}: {
  article: Partial<ArticleRequest>;
}): Promise<{ draft: ArticleDraftResponse; guide: ArticleResponse }> => {
  return await post(`/api/guides`, article);
};

export const uploadBlob = async ({
  file,
}: {
  file: File;
}): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  return await post(`/api/blob-resources/upload-blob-resource`, formData);
};

export const deleteBlob = async ({
  blobName,
}: {
  blobName: string;
}): Promise<{ blobName: string }> => {
  return await del(`/api/blob-resources/delete-blob-resource`, {
    params: { blobName },
  });
};

export const updateDraftArticle = async ({
  draftId,
  draftArticle,
}: {
  draftId: number;
  draftArticle: Partial<ArticleDraftRequest>;
}) => {
  return put<ArticleDraftResponse>(
    `/api/guides/draft/${draftId}`,
    draftArticle,
  );
};

export const publishArticle = async ({
  article,
}: {
  article: ArticleRequest;
}): Promise<ArticleResponse> => {
  return await post(`/api/guides`, article);
};

export const createDraft = async ({
  article,
}: {
  article: ArticleDraftRequest;
}): Promise<ArticleResponse> => {
  return await post<ArticleDraftResponse>(`/api/guides/draft`, article);
};

export const checkExistsDraftByGuideId = async (
  guideId: number,
): Promise<{ exists: boolean; draftId: number | null }> => {
  return await post(`/api/guides/draft/exists/${guideId}`);
};

export const deleteArticle = async (id: number): Promise<void> => {
  return await del(`/api/guides/${id}`);
};

export const deleteDraftArticle = async (draftId: number): Promise<void> => {
  return await del(`/api/guides/draft/${draftId}`);
};

export const isLoggedIn = async (): Promise<{ isLoggedIn: boolean }> => {
  return await get(`/api/auth/isLoggedIn`);
};

export const logout = async (): Promise<{ message: string }> => {
  return await post(`/api/auth/logout`);
};

// HR Psychological Safety Types
export type TeamHealthPattern =
  | 'persistent_problem'
  | 'temporary_dip'
  | 'stable_good'
  | 'stable_struggling'
  | 'improving'
  | 'insufficient_data';

export type TrendDirection =
  | 'improving'
  | 'stable'
  | 'declining'
  | 'insufficient_data';

export type ScoreStatus = 'success' | 'warning' | 'danger' | 'zero';

export type HrTeamSummary = {
  teamId: number;
  teamName: string;
  currentStatus: ScoreStatus;
  currentScore: number | null;
  currentStandardDeviation: number | null;
  /** Skåren fra forrige måling med nok svar, for endringen siden sist */
  previousScore: number | null;
  trend: TrendDirection;
  pattern: TeamHealthPattern;
  lastMeasurementDate: string | null;
  daysSinceLastMeasurement: number;
  hasScheduledSurvey: boolean;
  hasOngoingSurvey: boolean;
  respondentCount: number | null;
  teamMemberCount: number;
};

export type CrossTeamInsights = {
  averageScore: number;
  averageStandardDeviation: number;
  statusDistribution: {
    success: number;
    warning: number;
    danger: number;
  };
  totalTeams: number;
};

export type Alerts = {
  missingMeasurements: HrTeamSummary[];
  noScheduledSurveys: HrTeamSummary[];
};

export type CategoryInsight = {
  category: string;
  description: string;
  averageScore: number;
  averageStandardDeviation: number | null;
  teamCount: number;
};

export type QuestionInsight = {
  question: string;
  averageScore: number;
  averageStandardDeviation: number | null;
  teamCount: number;
};

export type OrganizationalInsights = {
  categories: CategoryInsight[];
  questions: QuestionInsight[];
};

export type HrPsychologicalSafetyOverview = {
  teams: HrTeamSummary[];
  crossTeamInsights: CrossTeamInsights;
  organizationalInsights: OrganizationalInsights;
  alerts: Alerts;
};

export type HrPsychologicalSafetyQuery = {
  fromDate?: string;
  toDate?: string;
};

export const getHrPsychologicalSafetyOverview = async (
  query?: HrPsychologicalSafetyQuery,
): Promise<HrPsychologicalSafetyOverview> => {
  const params = new URLSearchParams();
  if (query?.fromDate) params.append('fromDate', query.fromDate);
  if (query?.toDate) params.append('toDate', query.toDate);

  const queryString = params.toString();
  const url = `/api/hr/psychological-safety/overview${queryString ? `?${queryString}` : ''}`;

  return await get(url);
};

export type TeamSchedulingSettings = {
  id: number;
  teamId: number;
  workdayStartHour: number | null;
  workdayStartMinute: number | null;
  workdayEndHour: number | null;
  workdayEndMinute: number | null;
  skipWeekends: boolean | null;
  skipHolidays: boolean | null;
  holidayCountryCode: string | null;
  createdAt: Date;
  updatedAt: Date;
};

export type OrganizationSchedulingSettings = {
  id: number;
  organizationId: number;
  workdayStartHour: number | null;
  workdayStartMinute: number | null;
  workdayEndHour: number | null;
  workdayEndMinute: number | null;
  skipWeekends: boolean | null;
  skipHolidays: boolean | null;
  holidayCountryCode: string | null;
  createdAt: Date;
  updatedAt: Date;
};

export type EffectiveSchedulingSettings = {
  workdayStartHour: number;
  workdayStartMinute: number;
  workdayEndHour: number;
  workdayEndMinute: number;
  skipWeekends: boolean;
  skipHolidays: boolean;
  holidayCountryCode: string;
  source: 'team' | 'organization' | 'system';
};

export type TeamSchedulingSettingsInput = {
  workdayStartHour?: number | null;
  workdayStartMinute?: number | null;
  workdayEndHour?: number | null;
  workdayEndMinute?: number | null;
  skipWeekends?: boolean | null;
  skipHolidays?: boolean | null;
  holidayCountryCode?: string | null;
};

export type OrganizationSchedulingSettingsInput = {
  workdayStartHour?: number | null;
  workdayStartMinute?: number | null;
  workdayEndHour?: number | null;
  workdayEndMinute?: number | null;
  skipWeekends?: boolean | null;
  skipHolidays?: boolean | null;
  holidayCountryCode?: string | null;
};

export const getEffectiveTeamSchedulingSettings = async (
  teamId: number,
): Promise<EffectiveSchedulingSettings> => {
  return get<EffectiveSchedulingSettings>(
    `/api/scheduling-settings/teams/${teamId}/effective`,
  );
};

export const getTeamSchedulingSettings = async (
  teamId: number,
): Promise<TeamSchedulingSettings | null> => {
  try {
    return await get<TeamSchedulingSettings>(
      `/api/scheduling-settings/teams/${teamId}`,
    );
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateTeamSchedulingSettings = async (
  teamId: number,
  settings: TeamSchedulingSettingsInput,
): Promise<TeamSchedulingSettings> => {
  return put<TeamSchedulingSettings>(
    `/api/scheduling-settings/teams/${teamId}`,
    settings,
  );
};

export const deleteTeamSchedulingSettings = async (
  teamId: number,
): Promise<void> => {
  return del(`/api/scheduling-settings/teams/${teamId}`);
};

export const getCurrentOrganizationSchedulingSettings = async (): Promise<OrganizationSchedulingSettings | null> => {
  try {
    return await get<OrganizationSchedulingSettings>(
      `/api/scheduling-settings/organization`,
    );
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateCurrentOrganizationSchedulingSettings = async (
  settings: OrganizationSchedulingSettingsInput,
): Promise<OrganizationSchedulingSettings> => {
  return put<OrganizationSchedulingSettings>(
    `/api/scheduling-settings/organization`,
    settings,
  );
};

export const getCurrentOrganization = async (): Promise<Organization | null> => {
  try {
    return await get<Organization>('/api/organizations/current');
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateCurrentOrganization = async (
  data: { workLanguage: 'NB' | 'EN' | null },
): Promise<Organization> => {
  const currentOrg = await getCurrentOrganization();
  if (!currentOrg) {
    throw new Error('No organization found');
  }
  return patch<Organization>(`/api/organizations/${currentOrg.id}`, data);
};

export const getOrganizationSchedulingSettings = async (
  organizationId: number,
): Promise<OrganizationSchedulingSettings | null> => {
  try {
    return await get<OrganizationSchedulingSettings>(
      `/api/scheduling-settings/organizations/${organizationId}`,
    );
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateOrganizationSchedulingSettings = async (
  organizationId: number,
  settings: OrganizationSchedulingSettingsInput,
): Promise<OrganizationSchedulingSettings> => {
  return put<OrganizationSchedulingSettings>(
    `/api/scheduling-settings/organizations/${organizationId}`,
    settings,
  );
};

export type SmsQuotaStatus = {
  allowed: boolean;
  remaining: number;
  limit: number;
  used: number;
  percentage: number;
};

export const getSmsQuota = async (): Promise<SmsQuotaStatus> => {
  return await get('/api/sms-quota');
};

export type SeatQuotaStatus = {
  allowed: boolean;
  remaining: number;
  limit: number;
  used: number;
  percentage: number;
};

export const getSeatQuota = async (): Promise<SeatQuotaStatus> => {
  return await get('/api/seat-quota');
};

export type TenantListItem = {
  id: number;
  name: string;
  createdAt: string;
  smsQuota: number | null;
  smsQuotaResetAt: string | null;
  availableSeats: number | null;
  userCount: number;
  teamCount: number;
  smsUsed: number;
  seatsUsed: number;
  partnerId: number | null;
  partnerName: string | null;
};

export type TenantListResponse = {
  tenants: TenantListItem[];
  total: number;
};

export const getTenants = async (): Promise<TenantListResponse> => {
  return await get('/api/system-admin/tenants');
};

export type TenantFeature = {
  feature: string;
  enabledAt: string;
};

export type TenantFeaturesResponse = {
  tenantId: number;
  tenantName: string;
  features: TenantFeature[];
};

export const getTenantFeatures = async (
  tenantId: number,
): Promise<TenantFeaturesResponse> => {
  return await get(`/api/system-admin/tenants/${tenantId}/features`);
};

export const addTenantFeature = async (
  tenantId: number,
  feature: string,
): Promise<TenantFeature> => {
  return await post(`/api/system-admin/tenants/${tenantId}/features`, {
    feature,
  });
};

export const removeTenantFeature = async (
  tenantId: number,
  feature: string,
): Promise<void> => {
  return await post(
    `/api/system-admin/tenants/${tenantId}/features/${feature}/remove`,
    {},
  );
};

export type SurveyMetrics = {
  completed: number;
  planned: number;
};

export type SmsMetrics = {
  used: number;
  quota: number;
};

export type GrowthMetrics = {
  tenantsLastMonth: number;
  usersLastMonth: number;
};

export type SystemAdminDashboardMetrics = {
  totalTenants: number;
  totalUsers: number;
  totalTeamMembers: number;
  totalPartnerUsers: number;
  surveys: SurveyMetrics;
  sms: SmsMetrics;
  growth: GrowthMetrics;
};

export const getSystemAdminDashboardMetrics =
  async (): Promise<SystemAdminDashboardMetrics> => {
    return await get('/api/system-admin/dashboard/metrics');
  };

export type PartnerUser = {
  id: number;
  email: string;
  role: string;
  isPending: boolean;
  createdAt: string;
};

export type PartnerCustomer = {
  id: number;
  name: string;
  userCount: number;
  teamCount: number;
  status: 'Active' | 'Inactive';
  createdAt: string;
};

export type PartnerStats = {
  totalCustomers: number;
  totalEndUsers: number;
  totalTeams: number;
  activeCustomers: number;
};

export type PartnerDetail = {
  id: number;
  name: string;
  orgNumber: string;
  cashbackPercentage: number | null;
  createdAt: string;
  stats: PartnerStats;
  users: PartnerUser[];
  customers: PartnerCustomer[];
};

export const getPartnerDetail = async (
  partnerId: number,
): Promise<PartnerDetail> => {
  return await get(`/api/system-admin/partners/${partnerId}`);
};

export type TenantUser = {
  id: number;
  email: string;
  role: string;
  isPending: boolean;
  createdAt: string;
};

export type TenantTeam = {
  id: number;
  name: string;
  numberOfTeamMembers: number;
  createdAt: string;
};

export type TenantOrganization = {
  id: number;
  name: string;
  teamCount: number;
  createdAt: string;
};

export type TenantSmsStats = {
  smsQuota: number | null;
  smsUsed: number;
  smsQuotaResetAt: string | null;
  percentageUsed: number | null;
};

export type TenantSeatsStats = {
  availableSeats: number | null;
  seatsUsed: number;
  percentageUsed: number | null;
};

export type TenantDetail = {
  id: number;
  name: string;
  partnerId: number | null;
  partnerName: string | null;
  isPartner: boolean;
  createdAt: string;
  smsStats: TenantSmsStats;
  seatsStats: TenantSeatsStats;
  users: TenantUser[];
  teams: TenantTeam[];
  organizations: TenantOrganization[];
};

export const getTenantDetail = async (
  tenantId: number,
): Promise<TenantDetail> => {
  return await get(`/api/system-admin/tenants/${tenantId}`);
};

export type UserTenant = {
  id: number;
  name: string;
  partnerName: string | null;
};

export type UserRole = {
  role: string;
  tenantId: number;
  tenantName: string;
  partnerId: number | null;
  partnerName: string | null;
  createdAt: string;
};

export type UserTeamMembership = {
  teamId: number;
  teamName: string;
  organizationId: number;
  organizationName: string;
  isLeader: boolean;
  role: string;
};

export type UserStats = {
  totalTeamMemberships: number;
  leaderOfTeams: number;
  totalRoles: number;
};

export type UserDetail = {
  id: number;
  email: string;
  isPending: boolean;
  createdAt: string;
  deletedAt: string | null;
  tenant: UserTenant;
  roles: UserRole[];
  teamMemberships: UserTeamMembership[];
  stats: UserStats;
};

export const getUserDetail = async (userId: number): Promise<UserDetail> => {
  return await get(`/api/system-admin/users/${userId}`);
};

export type UpdateTenantConfigPayload = {
  smsQuota?: number | null;
  smsQuotaResetAt?: string | null;
  availableSeats?: number | null;
};

export const updateTenantConfig = async (
  tenantId: number,
  payload: UpdateTenantConfigPayload,
): Promise<TenantListItem> => {
  return await patch(`/api/system-admin/tenants/${tenantId}`, payload);
};

export type CreateTenantPayload = {
  tenantName: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  orgNumber?: string;
  smsQuota?: number | null;
  availableSeats?: number | null;
};

export const createTenant = async (
  payload: CreateTenantPayload,
): Promise<TenantListItem> => {
  return await post('/api/system-admin/tenants', payload);
};

export type PartnerListItem = {
  id: number;
  name: string;
  contactEmail: string;
  contactPhone: string | null;
  cashbackPercentage: number | null;
  createdAt: string;
  customerCount: number;
};

export type PartnerListResponse = {
  partners: PartnerListItem[];
  total: number;
};

export const getPartners = async (): Promise<PartnerListResponse> => {
  return await get('/api/system-admin/partners');
};

export type Partner = {
  id: number;
  name: string;
  contactEmail: string;
  contactPhone: string | null;
  cashbackPercentage: number | null;
  createdAt: string;
};

export type CreatePartnerPayload = {
  name: string;
  contactEmail: string;
  contactPhone?: string;
  cashbackPercentage?: number;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
};

export const createPartner = async (
  payload: CreatePartnerPayload,
): Promise<Partner> => {
  return await post('/api/system-admin/partners', payload);
};

export type UpdatePartnerPayload = {
  contactEmail?: string;
  contactPhone?: string;
  cashbackPercentage?: number;
};

export const updatePartner = async (
  partnerId: number,
  payload: UpdatePartnerPayload,
): Promise<PartnerDetail> => {
  return await patch(`/api/system-admin/partners/${partnerId}`, payload);
};

export type UpgradeTenantToPartnerPayload = {
  cashbackPercentage: number;
  contactEmail?: string;
  contactPhone?: string;
  adminUserIds: number[];
};

export type UpgradeTenantToPartnerResponse = {
  success: boolean;
  partnerId: number;
  emailsSent: number;
};

export const upgradeTenantToPartner = async (
  tenantId: number,
  payload: UpgradeTenantToPartnerPayload,
): Promise<UpgradeTenantToPartnerResponse> => {
  return await patch(
    `/api/system-admin/tenants/${tenantId}/upgrade-to-partner`,
    payload,
  );
};

export type PartnerCustomerListItem = {
  id: number;
  name: string;
  createdAt: string;
  smsQuota: number | null;
  smsQuotaResetAt: string | null;
  availableSeats: number | null;
  pricePerSeat: number | null;
  billingPeriod: string | null;
  userCount: number;
  teamCount: number;
  smsUsed: number;
  seatsUsed: number;
  monthlyCashbackNOK: number;
  yearlyCashbackNOK: number;
};

export type PartnerCustomerListResponse = {
  customers: PartnerCustomerListItem[];
  total: number;
  partnerInfo: {
    id: number;
    name: string;
  };
};

export const getPartnerCustomers =
  async (): Promise<PartnerCustomerListResponse> => {
    return await get('/api/partner-admin/customers');
  };

export type CreateCustomerForPartnerPayload = {
  tenantName: string;
  orgNumber: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  smsQuota?: number | null;
  availableSeats?: number | null;
  pricePerSeat?: number | null;
  billingPeriod?: string | null;
  billingStartDate?: string | null;
};

export const createCustomerForPartner = async (
  payload: CreateCustomerForPartnerPayload,
): Promise<PartnerCustomerListItem> => {
  return await post('/api/partner-admin/customers', payload);
};

export type PartnerCustomerDetail = {
  id: number;
  name: string;
  createdAt: Date;
  status: 'active' | 'inactive';
  plan: string;
  pricePerSeat: number | null;
  billingPeriod: string | null;
  billingStartDate: Date | null;
  smsQuota: number | null;
  smsQuotaResetAt: Date | null;
  availableSeats: number | null;
  userCount: number;
  teamCount: number;
  smsUsed: number;
  seatsUsed: number;
  cashbackPercentage: number | null;
  monthlyCashbackNOK: number;
  yearlyCashbackNOK: number;
  surveys: {
    completed: Array<{
      id: number;
      name: string;
      type: string;
      completedAt: Date;
      responseCount: number;
    }>;
    planned: Array<{
      id: number;
      name: string;
      type: string;
      scheduledFor: Date;
      targetParticipants: number;
    }>;
  };
  additionalServices: Array<{
    id: number;
    name: string;
    status: 'active' | 'inactive';
    purchasedAt: Date;
  }>;
};

export const getPartnerCustomerDetail = async (
  customerId: number,
): Promise<PartnerCustomerDetail> => {
  return await get(`/api/partner-admin/customers/${customerId}`);
};

export type UpdateCustomerPricingPayload = {
  pricePerSeat?: number | null;
  availableSeats?: number | null;
  billingPeriod?: 'monthly' | 'yearly' | null;
  billingStartDate?: string | null;
};

export const updateCustomerPricing = async (
  customerId: number,
  payload: UpdateCustomerPricingPayload,
): Promise<void> => {
  return await patch(
    `/api/partner-admin/customers/${customerId}/pricing`,
    payload,
  );
};

export type PartnerDashboard = {
  partnerInfo: {
    id: number;
    name: string;
    cashbackPercentage: number | null;
  };
  stats: {
    totalCustomers: number;
    activeCustomers: number;
    totalUsers: number;
    totalTeams: number;
    completedSurveys: number;
    plannedSurveys: number;
  };
  licenses: {
    total: number;
    used: number;
  };
  sms: {
    quota: number;
    used: number;
  };
  cashback: {
    monthlyCashbackNOK: number;
    yearlyCashbackNOK: number;
  };
};

export const getPartnerDashboard = async (): Promise<PartnerDashboard> => {
  return await get('/api/partner-admin/dashboard');
};

export type CashbackStatus =
  | 'active'
  | 'not_started'
  | 'expired'
  | 'expiring_soon';

export type CustomerCashback = {
  customerId: number;
  customerName: string;
  monthlyCashbackNOK: number;
  yearlyCashbackNOK: number;
  remainingCashbackNOK: number;
  status: CashbackStatus;
  monthsRemaining: number | null;
  billingPeriod: string | null;
  billingStartDate: string | null;
  contractEndDate: string | null;
  pricePerSeat: number | null;
  availableSeats: number | null;
};

export type UpcomingChange = {
  date: string;
  type: 'contract_expiry' | 'billing_start';
  customerId: number;
  customerName: string;
  impactMonthlyNOK: number;
  description: string;
};

export type PartnerCashbackSummary = {
  summary: {
    totalMonthlyNOK: number;
    totalYearlyNOK: number;
    totalRemainingNOK: number;
    activeCustomerCount: number;
    expiringCustomerCount: number;
    notStartedCustomerCount: number;
    expiredCustomerCount: number;
  };
  customers: CustomerCashback[];
  upcomingChanges: UpcomingChange[];
  partnerInfo: {
    id: number;
    name: string;
    cashbackPercentage: number | null;
  };
};

export const getPartnerCashbackSummary =
  async (): Promise<PartnerCashbackSummary> => {
    return await get('/api/partner-admin/cashback');
  };

// ============================================================================
// PARTNER ADMIN - LEADS
// ============================================================================

export type LeadStatus =
  | 'NEW'
  | 'CONTACTED'
  | 'QUALIFIED'
  | 'PROPOSAL_SENT'
  | 'NEGOTIATING'
  | 'WON'
  | 'LOST'
  | 'ON_HOLD';

export type LeadQuality = 'HOT' | 'WARM' | 'COLD';

export type LeadActivityType =
  | 'NOTE'
  | 'EMAIL'
  | 'PHONE_CALL'
  | 'MEETING'
  | 'STATUS_CHANGE'
  | 'PRICE_CHANGED';

export type LeadActivity = {
  id: number;
  type: LeadActivityType;
  description: string;
  createdAt: string;
  userName: string;
};

export type Lead = {
  id: number;
  companyName: string;
  contactPersonName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: LeadStatus;
  quality: LeadQuality | null;
  source: string | null;
  estimatedPricePerSeat: number | null;
  estimatedSeats: number | null;
  estimatedBillingPeriod: string | null;
  estimatedMonthlyValueNOK: number;
  estimatedYearlyValueNOK: number;
  estimatedMonthlyCashbackNOK: number;
  estimatedYearlyCashbackNOK: number;
  nextFollowUpDate: Date | null;
  daysUntilFollowUp: number | null;
  createdAt: Date;
  lastActivityDate: Date | null;
  convertedToTenantId: number | null;
  activities?: LeadActivity[];
  notes?: string | null;
};

export type LeadListResponse = {
  leads: Lead[];
  total: number;
  partnerInfo: {
    id: number;
    name: string;
  };
};

export type PipelineStats = {
  totalLeads: number;
  totalEstimatedMonthlyValueNOK: number;
  totalEstimatedYearlyValueNOK: number;
  totalEstimatedMonthlyCashbackNOK: number;
  totalEstimatedYearlyCashbackNOK: number;
  lostOpportunitiesCount: number;
  lostOpportunitiesMonthlyCashbackNOK: number;
  lostOpportunitiesYearlyCashbackNOK: number;
  byStatus: Array<{
    status: LeadStatus;
    count: number;
    estimatedMonthlyValueNOK: number;
    estimatedYearlyValueNOK: number;
  }>;
  byQuality: Array<{
    quality: LeadQuality | 'UNSET';
    count: number;
    estimatedMonthlyValueNOK: number;
    estimatedYearlyValueNOK: number;
  }>;
  conversionRate: number | null;
  averageDaysToConversion: number | null;
  upcomingFollowUps: Array<{
    leadId: number;
    companyName: string;
    followUpDate: Date;
    daysUntil: number;
    estimatedMonthlyValueNOK: number;
  }>;
};

export type CreateLeadPayload = {
  companyName: string;
  contactPersonName?: string;
  contactEmail?: string;
  contactPhone?: string;
  source?: string;
  estimatedPricePerSeat?: number; // In øre
  estimatedSeats?: number;
  estimatedBillingPeriod?: 'monthly' | 'yearly';
  estimatedBillingStartDate?: string;
  estimatedContractEndDate?: string;
  estimatedSmsQuota?: number;
  notes?: string;
  nextFollowUpDate?: string;
};

export type ConvertLeadPayload = {
  name?: string;
  pricePerSeat?: number;
  availableSeats?: number;
  billingPeriod?: 'monthly' | 'yearly';
  billingStartDate?: string;
  contractEndDate?: string;
  smsQuota?: number;
};

export type UpdateLeadPayload = {
  companyName?: string;
  contactPersonName?: string;
  contactEmail?: string;
  contactPhone?: string;
  source?: string;
  status?: LeadStatus;
  estimatedPricePerSeat?: number;
  estimatedSeats?: number;
  estimatedBillingPeriod?: 'monthly' | 'yearly';
  estimatedBillingStartDate?: string;
  estimatedContractEndDate?: string;
  estimatedSmsQuota?: number;
  notes?: string;
  nextFollowUpDate?: string;
};

export const getLeads = async (filters?: {
  status?: string;
  search?: string;
}): Promise<LeadListResponse> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);

  const url = params.toString()
    ? `/api/partner-admin/leads?${params}`
    : '/api/partner-admin/leads';
  return await get(url);
};

export const getPipelineStats = async (): Promise<PipelineStats> => {
  return await get('/api/partner-admin/leads/stats');
};

export const createLead = async (payload: CreateLeadPayload): Promise<Lead> => {
  return await post('/api/partner-admin/leads', payload);
};

export const getLeadDetail = async (leadId: number): Promise<Lead> => {
  return await get(`/api/partner-admin/leads/${leadId}`);
};

export const updateLead = async (
  leadId: number,
  payload: UpdateLeadPayload,
): Promise<Lead> => {
  return await patch(`/api/partner-admin/leads/${leadId}`, payload);
};

export const convertLeadToCustomer = async (
  leadId: number,
  payload?: ConvertLeadPayload,
): Promise<{ tenantId: number }> => {
  return await post(
    `/api/partner-admin/leads/${leadId}/convert`,
    payload ?? {},
  );
};

export type AddLeadActivityPayload = {
  type: LeadActivityType;
  description: string;
};

export const addLeadActivity = async (
  leadId: number,
  payload: AddLeadActivityPayload,
): Promise<void> => {
  return await post(`/api/partner-admin/leads/${leadId}/activities`, payload);
};

export const deleteLead = async (leadId: number): Promise<void> => {
  return await del(`/api/partner-admin/leads/${leadId}`);
};

// ============================================================================
// SYSTEM ADMIN - PARTNER LEADS OVERVIEW
// ============================================================================

export type PartnerLeadsStatusDistribution = {
  status: string;
  count: number;
  valueNOK: number;
};

export type PartnerLeadsTopPartnerStatusBreakdown = {
  status: string;
  count: number;
  valueNOK: number;
};

export type PartnerLeadsTopPartner = {
  partnerId: number;
  partnerName: string;
  totalLeads: number;
  pipelineValueNOK: number;
  cashbackValueNOK: number;
  statusBreakdown: PartnerLeadsTopPartnerStatusBreakdown[];
};

export type PartnerLeadsOverview = {
  totalPartners: number;
  totalLeads: number;
  totalPipelineValueNOK: number;
  totalCashbackValueNOK: number;
  statusDistribution: PartnerLeadsStatusDistribution[];
  topPartners: PartnerLeadsTopPartner[];
};

export const getPartnerLeadsOverview =
  async (): Promise<PartnerLeadsOverview> => {
    return await get('/api/system-admin/partner-leads/overview');
  };

export type TrialStatusType = 'trial' | 'paid' | 'expired' | 'cancelled';
export type TrialUrgencyLevel = 'normal' | 'warning' | 'urgent';

export type TrialStatus = {
  status: TrialStatusType;
  daysRemaining: number | null;
  trialStartDate: string | null;
  trialEndDate: string | null;
  urgencyLevel: TrialUrgencyLevel;
  isExpired: boolean;
  isEndingSoon: boolean;
};

export const getTrialStatus = async (): Promise<TrialStatus> => {
  return await get('/api/subscription/trial-status');
};
